import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import "../Components/Preferences/PreferencesPage.css";
import PopupConfirm from "../Components/Preferences/PopupConfirm.js";
import PopupMessage from "../Components/Preferences/PopupMessage.js";
import {
  LOAD_USER_SUCCESS,
  LOGOUT_SUCCESS,
  UPDATE_PREFERENCES_SUCCESS,
} from "../Constants/userConstants.js";

import {
  TC_INVALIDATED,
  TC_UNSAVED_CLEAR,
} from "../Constants/tcConstants.js";

import {
  C_INVALIDATED,
} from "../Constants/cConstants.js";

import { useDispatch, useSelector } from "react-redux";

const Preferences = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isAuth, user } = useSelector((state) => state.user);

  const [formState, setFormState] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorAuth: false,
    // pushNotification: false,
    emailNotification: false,
    textSize: 'Medium',
  });

  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [navigateToSignIn, setNavigateToSignIn] = useState(false);
  const [triggerTwoFactorSetup, setTriggerTwoFactorSetup] = useState(false);

  useEffect(() => {
    if (user && user.preferences){
      const textSizeArr = ["Small", "Medium", "Large"];
      let twoFac = (user.preferences.twoFac !== undefined) ? user.preferences.twoFac : false;
      // let pushNoti = (user.preferences.pushNoti !== undefined) ? user.preferences.pushNoti : false;
      let emailNoti = (user.preferences.emailNoti !== undefined) ? user.preferences.emailNoti : false;
      let textSize = (user.preferences.textSize !== undefined) ? textSizeArr[Math.max(Math.min(user.preferences.textSize, 2), 0)] : "Medium";
      setFormState(prevFormState => ({
        ...prevFormState,
        twoFactorAuth: twoFac,
        // pushNotification: pushNoti,
        emailNotification: emailNoti,
        textSize: textSize,
      }));
    }
    if (!isAuth){
      navigate('/');
    }
  }, [isAuth, user, navigate]);
  //navigate does not have to be added unless using relative links.

  useEffect(() => {
    if (isAuth && user && user.preferences) {
      const textSize = user.preferences.textSize;

      switch (textSize) {
        case 0: // Small
            document.documentElement.style.setProperty('--font-size-header', '1.5vw');
            document.documentElement.style.setProperty('--font-size-label', '14px');
            document.documentElement.style.setProperty('--font-size-input', '14px');
            document.documentElement.style.setProperty('--font-size-button', '0.8vw');
            break;
        case 1: // Medium (default)
            document.documentElement.style.setProperty('--font-size-header', '2vw');
            document.documentElement.style.setProperty('--font-size-label', '16px');
            document.documentElement.style.setProperty('--font-size-input', '16px');
            document.documentElement.style.setProperty('--font-size-button', '1vw');
            break;
        case 2: // Large
            document.documentElement.style.setProperty('--font-size-header', '3vw');
            document.documentElement.style.setProperty('--font-size-label', '24px');
            document.documentElement.style.setProperty('--font-size-input', '24px');
            document.documentElement.style.setProperty('--font-size-button', '2vw');
            break;
        default:
          break;
      }
    } else if (!isAuth) { 
      document.documentElement.style.setProperty('--font-size-header', '2vw');
      document.documentElement.style.setProperty('--font-size-label', '16px');
      document.documentElement.style.setProperty('--font-size-input', '16px');
      document.documentElement.style.setProperty('--font-size-button', '1vw');
    }
  }, [user, isAuth]);
  
  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState({
      ...formState,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handlePreferencesSubmit = async (event) => {
    event.preventDefault();

    const preferences = {
      emailNoti: formState.emailNotification,
      // pushNoti: formState.pushNotification,
      twoFac: formState.twoFactorAuth,
      textSize: formState.textSize,
    };

    try {
      const response = await axios.put('/api/v1/users/me/update', preferences);
      setMessageContent('Your changes have been saved!');
      setIsMessageOpen(true);
      setNavigateToSignIn(false);

      const loadUser = await axios.get('api/v1/users/me');
      if (loadUser.data.user){
        dispatch({ type: LOAD_USER_SUCCESS, payload: loadUser.data.user });
      }
      
      dispatch({
        type: UPDATE_PREFERENCES_SUCCESS,
        payload: { preferences: response.data.user.preferences },
      });

      console.log("Response status:", response.status);
      console.log('Preferences saved:', response.data);
    }
    catch (error) {
      if (error.response) {
        console.log(error.response.status);
        console.log(error.response.data);
        console.log(error.response.headers);
      } else if (error.request) {
        console.log("No response from server");
      } else {
        console.log("Error setting up request");
      }
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();

    if (formState.newPassword !== formState.confirmPassword) {
      setMessageContent("New password and confirm password do not match.");
      setIsMessageOpen(true);
      return;
    }

    try {
      await axios.put('/api/v1/users/password/update', {
        oldPassword: formState.currentPassword,
        newPassword: formState.newPassword,
        confirmPassword: formState.confirmPassword,
      });

      setMessageContent('Password changed successfully!');
      setIsMessageOpen(true);
      setFormState({ ...formState, currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      if (error.response) {
        setMessageContent(error.response.data.message || 'Error changing password.');
      } else {
        setMessageContent('Error changing password. Please try again.');
      }
      setIsMessageOpen(true);
    }
  };

  const handleDeleteAllUserData = async () => {
    try {
      const response = await axios.delete('/api/v1/users/me/deleteData', {
        withCredentials: true,
      });

      if (response.status !== 200) {
        throw new Error('Failed to delete all user data');
      }

      console.log('All user data deleted');
      
      dispatch({type: TC_INVALIDATED});
      dispatch({type: TC_UNSAVED_CLEAR});
      dispatch({type: C_INVALIDATED});

      setMessageContent('All of your data has been deleted!');
      setIsMessageOpen(true);
      setNavigateToSignIn(false);
    } catch (error) {
      console.error('Error deleting all user data:', error);
      setMessageContent('Failed to delete data. Please try again.');
      setIsMessageOpen(true);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const response = await axios.delete('/api/v1/users/me/deleteMe', {
        withCredentials: true,
      });

      console.log("Response status:", response.status); // Log the response status
      console.log('Delete account response:', response.data); // Log the response data

      if (response.status !== 200) {
        throw new Error('Failed to delete account');
      }

      console.log('User account deleted');
      setMessageContent('Your account has been deleted!');
      setIsMessageOpen(true);
      setNavigateToSignIn(true);
    } catch (error) {
      console.error('Error deleting account:', error);
      setMessageContent('Failed to delete account. Please try again.');
      setIsMessageOpen(true);
    }
  };

  const closeMessage = () => {
    setIsMessageOpen(false);
    if (navigateToSignIn) {
      dispatch({type: TC_INVALIDATED});
      dispatch({type: TC_UNSAVED_CLEAR});
      dispatch({type: C_INVALIDATED});
      navigate('/signin');
      dispatch({type: LOGOUT_SUCCESS});
    }
    if (triggerTwoFactorSetup) {
      setTriggerTwoFactorSetup(false);
    }
  };

  const handleGoBack = () => {
    navigate('/profile');
  };

  return (
    <div className="preferences">
      <h1>Account Settings</h1>
      <div className="columns">
        <div className="column">
          <form onSubmit={handlePreferencesSubmit}>
            <h2>Notifications Settings:</h2>
            {/* <div>
              <label>
                Push Notification:
                <input
                  type="checkbox"
                  name="pushNotification"
                  checked={formState.pushNotification}
                  onChange={handleChange}
                />
              </label>
            </div> */}
            <div>
              <label>
                Email Notification:
                <input
                  type="checkbox"
                  name="emailNotification"
                  checked={formState.emailNotification}
                  onChange={handleChange}
                />
              </label>
            </div>

            <h2>Text Size:</h2>
            <div>
            <label>
                Small
                <input
                  type="radio"
                  name="textSize"
                  value="Small"
                  checked={formState.textSize === 'Small'}
                  onChange={handleChange}
                />
              </label>
            </div>
            <div>
              <label>
                Medium
                <input
                  type="radio"
                  name="textSize"
                  value="Medium"
                  checked={formState.textSize === 'Medium'}
                  onChange={handleChange}
                />
              </label>
            </div>
            <div>
              <label>
                Large
                <input
                  type="radio"
                  name="textSize"
                  value="Large"
                  checked={formState.textSize === 'Large'}
                  onChange={handleChange}
                />
              </label>
            </div>
            <div className="two-factor-section">
              <h2>Two-Factor Authentication:</h2>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  name="twoFactorAuth"
                  checked={formState.twoFactorAuth}
                  onChange={handleChange}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="form-buttons">
              <button type="submit" className="save-button">Save Changes</button>
            </div>
          </form>
          <PopupConfirm
                label="Go Back"
                className="goBack-button"
                onConfirm={handleGoBack}
                message="Are you sure you want to go back? Your changes could not be saved."
              />
        </div>

        <div className="column">
          <form onSubmit={handleChangePassword}>
            <h2>Change Password:</h2>
            <div>
              <label>Current Password:</label>
              <input
                type="password"
                name="currentPassword"
                value={formState.currentPassword}
                onChange={handleChange}
              />
            </div>
            <div>
              <label>New Password:</label>
              <input
                type="password"
                name="newPassword"
                value={formState.newPassword}
                onChange={handleChange}
              />
            </div>
            <div>
              <label>Confirm New Password:</label>
              <input
                type="password"
                name="confirmPassword"
                value={formState.confirmPassword}
                onChange={handleChange}
              />
            </div>
            <button type="submit" className="save-button">Update Password</button>
          </form>

          <h2>Privacy Settings:</h2>
          <PopupConfirm
            label="Delete All User Data"
            className="deleteAll-button"
            onConfirm={handleDeleteAllUserData}
            message="Are you sure you want to delete all of your data?"
          />
          <PopupConfirm
            label="Delete Account"
            className="deleteAccount-button"
            onConfirm={handleDeleteAccount}
            message="Are you sure you want to delete your account permanently?"
          />
        </div>
      </div>

      <PopupMessage 
        message={messageContent} 
        isOpen={isMessageOpen} 
        onClose={closeMessage} 
      />
    </div>
  );
};

export default Preferences;